import { DecimalPipe } from '@angular/common';
import { Component } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, map, Observable, shareReplay, take } from 'rxjs';
import { CouponUsageLogModel } from '../../../../../models/coupon.model';
import { CouponService } from '../../../../../services/coupon.service';
import { CouponUsageDialogComponent } from '../../../../shared-components/coupon-usage-dialog/coupon-usage-dialog.component';
import {
  Filter,
  TableColumn,
} from '../../../../shared-components/custom-table/custom-table.component';

@Component({
  selector: 'app-coupon-usage-log',
  providers: [DecimalPipe],
  templateUrl: './coupon-usage-log.component.html',
  styleUrl: './coupon-usage-log.component.scss',
  standalone: false,
})
export class CouponUsageLogComponent {
  public isLoading = true;
  public couponUsageLogs: CouponUsageLogModel[] = [];
  public dataSource$: Observable<CouponUsageLogModel[]>;

  constructor(
    private couponService: CouponService,
    private dialogService: DialogService,
    private decimalPipe: DecimalPipe
  ) {
    this.getCouponUsageLogs();
  }

  private getCouponUsageLogs(): void {
    this.isLoading = true;
    this.dataSource$ = this.couponService.getCouponUsageLogs().pipe(
      take(1),
      map((couponUsageLogs: CouponUsageLogModel[]) => {
        this.isLoading = false;
        this.couponUsageLogs = couponUsageLogs;
        return this.couponUsageLogs;
      }),
      catchError(error => {
        this.isLoading = false;
        console.error('Error fetching coupon usage logs', error);
        return [];
      }),
      shareReplay(1) // Cache the result
    );
  }
  public columns: TableColumn[] = [
    {
      columnDef: 'title',
      header: 'Titel',
      width: '10%',
    },
    {
      columnDef: 'redeemed_by',
      header: 'Einlöser',
      filterValue: (row: CouponUsageLogModel) =>
        this.getRedeemedByFilterText(row),
      render: (row: CouponUsageLogModel) => this.getRedeemedByText(row),
      onCellClick: (row: CouponUsageLogModel) => this.onRedeemedByClick(row),
      width: '20%',
    },
    {
      columnDef: 'value',
      header: 'Sparen',
      width: '8%',
      render: (row: CouponUsageLogModel) => {
        return (
          this.decimalPipe.transform(row.value) +
          `${row.discountType === 'percentage' ? ' %' : ' €'}`
        );
      },
    },
    {
      columnDef: 'type',
      header: 'Sorte',
      filterValue: (row: CouponUsageLogModel) =>
        row.type === 'sellable' ? 'Verkaufbar' : 'Promo',
      render: (row: CouponUsageLogModel) =>
        row.type === 'sellable' ? 'Verkaufbar' : 'Promo',
      width: '10%',
      fontWeight: '400',
    },
    {
      columnDef: 'code',
      header: 'Code',
      width: '10%',
      fontWeight: '400',
    },
    {
      columnDef: 'restrictions',
      header: 'Einschränkungen',
      width: '22%',
      filterValue: (row: CouponUsageLogModel) =>
        this.getRestrictionsFilterText(row),
      render: (row: CouponUsageLogModel) => {
        return this.getRestrictionsText(row);
      },
      fontWeight: '400',
    },
    {
      columnDef: 'status',
      header: 'Status',
      width: '10%',
      filterValue: (row: CouponUsageLogModel) =>
        this.getCouponUsageStatusValue(row).status,
      render: (row: CouponUsageLogModel) => {
        const statusClass = this.getCouponUsageStatusValue(row).class;

        return `<div class="${statusClass}">
        ${this.getCouponUsageStatusValue(row).status}
      </div>`;
      },
    },
  ];

  // Define filter configuration
  public filters: Filter[] = [
    {
      filteredColumn: 'status',
      type: 'multiselect',
      selectOptions: [
        {
          value: 'used',
          label: 'Verwendet',
        },
        {
          value: 'deleted',
          label: 'Gelöscht',
        },
        {
          value: 'inactive',
          label: 'Inaktiv',
        },
        {
          value: 'sold',
          label: 'Verkauft',
        },
        {
          value: 'active',
          label: 'Aktiv',
        },
        {
          value: 'expired',
          label: 'Abgelaufen',
        },
      ],
      placeholder: 'Status auswählen...',
      label: 'Status',
    },
    {
      filteredColumn: 'type',
      type: 'select',
      selectOptions: [
        {
          value: 'sellable',
          label: 'Verkaufbar',
        },
        {
          value: 'promo',
          label: 'Promo',
        },
      ],
      placeholder: 'Sorte auswählen...',
      label: 'Sorte',
    },
  ];

  private getCouponUsageStatusValue(row: CouponUsageLogModel): {
    status: string;
    class: string;
  } {
    switch (row.status) {
      case 'used':
        return {
          status: 'Verwendet',
          class: 'inwi-chip-text-green-outline',
        };
      case 'deleted':
        return {
          status: 'Gelöscht',
          class: 'inwi-chip-text-red-outline',
        };
      case 'inactive':
        return {
          status: 'Inaktiv',
          class: 'inwi-chip-text-red-outline',
        };
      case 'sold':
        return {
          status: 'Verkauft',
          class: 'inwi-chip-text-yellow-outline',
        };
      case 'active':
        return {
          status: 'Aktiv',
          class: 'inwi-chip-text-green-outline',
        };
      case 'expired':
        return {
          status: 'Abgelaufen',
          class: 'inwi-chip-text-red-outline',
        };
      default:
        return {
          status: 'Unbekannt',
          class: 'inwi-chip-text-grey-outline',
        };
    }
  }

  private getRedeemedByText(row: CouponUsageLogModel): string {
    if (!row.redeemedBy || row.redeemedBy.length === 0) {
      return null;
    }
    if (row.redeemedBy && row.redeemedBy.length === 1) {
      return row.redeemedBy[0].email;
    }
    return `<div class="flex-r gap-8 justify-space-between"><u>${row.redeemedBy.length} Einlösungen</u><i class="fa-regular fa-circle-info"></i></div>`;
  }

  private getRedeemedByFilterText(row: CouponUsageLogModel): string {
    if (!row.redeemedBy || row.redeemedBy.length <= 1) {
      return null;
    }
    return row.redeemedBy.map(user => user.email).join(', ');
  }

  private onRedeemedByClick(row: CouponUsageLogModel): void {
    if (!row.redeemedBy || row.redeemedBy.length <= 1) {
      return;
    }
    this.dialogService.open(CouponUsageDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: { redeemedBy: row.redeemedBy },
    });
  }

  private getRestrictionsText(row: CouponUsageLogModel): string {
    const conditionTypeLabels: { [key: string]: string } = {
      regular: 'Regulär',
      early: 'Frühbucher',
      bed: 'BED',
      office: 'Praxis',
      logo: 'LOGO',
      vdb: 'VDB',
      vdp: 'VDP',
    };

    let seminarsHTML = '';
    if (
      row.restrictions?.allowedSeminars &&
      row.restrictions.allowedSeminars.length > 0
    ) {
      const seminarLinks = row.restrictions.allowedSeminars
        .map(seminar => {
          const seminarId = btoa(String(seminar.id));
          return `<a href="/administration/seminars/edit/${seminarId}" style="text-decoration: underline; color: var(--inwi-color-primary); text-underline-offset: 2px;" onclick="event.stopPropagation();">${seminar.title}</a>`;
        })
        .join(', ');
      seminarsHTML = `<span>Seminare: ${seminarLinks}</span>`;
      console.log(seminarsHTML);
    }

    const priceCategoriesHTML =
      row.restrictions?.allowedConditionTypes?.length > 0
        ? `<span>Preis-Kategorien: ${row.restrictions.allowedConditionTypes
            .map(type => conditionTypeLabels[type] || type)
            .join(', ')}</span>`
        : null;

    if (!seminarsHTML && !priceCategoriesHTML) {
      return '<span class="text-secondary">Keine</span>';
    }
    return `<div class="flex-c gap-8">${seminarsHTML || ''} ${priceCategoriesHTML || ''}</div>`;
  }

  private getRestrictionsFilterText(row: CouponUsageLogModel): string {
    if (!row.restrictions) {
      return '';
    }

    const parts: string[] = [];

    if (
      row.restrictions.allowedSeminars &&
      row.restrictions.allowedSeminars.length > 0
    ) {
      const seminarTitles = row.restrictions.allowedSeminars
        .map(s => s.title)
        .join(', ');
      parts.push(`Seminare: ${seminarTitles}`);
    }

    if (
      row.restrictions.allowedConditionTypes &&
      row.restrictions.allowedConditionTypes.length > 0
    ) {
      const conditionTypeLabels: { [key: string]: string } = {
        regular: 'Regulär',
        early: 'Frühbucher',
        bed: 'BED',
        office: 'Praxis',
        logo: 'LOGO',
        vdb: 'VDB',
        vdp: 'VDP',
      };
      const conditionLabels = row.restrictions.allowedConditionTypes.map(
        type => conditionTypeLabels[type] || type
      );
      parts.push(`Preis-Kategorien: ${conditionLabels.join(', ')}`);
    }

    return parts.join(' | ');
  }
}
